import { useEffect, useState, useCallback } from "react";
import api, { qs, apiError } from "@/lib/api";
import { monthOptions, monthLabel } from "@/lib/dpc";
import { fmtINR, fmtDay } from "@/lib/service";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WarrantyToggle, WarrantyStrip, basisText, useExWarranty } from "@/components/WarrantyToggle";
import { BayPanel } from "@/components/service-overview/BayPanel";
import { toast } from "sonner";
import { Download } from "lucide-react";

const TH = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap";
const heat = (rank, n) => (rank === 1 ? "bg-emerald-600 text-white" : rank <= 3 ? "bg-emerald-100 text-emerald-900" : rank > n - 3 ? "bg-red-100 text-red-900" : "");

function Block({ cat, cls, block, classes, catLabel, total }) {
  const meta = classes.find((c) => c.key === cls);
  const rows = [...block.rows].sort((a, b) => a.rank - b.rank);
  const n = total || rows.length;
  return (
    <div className="border bg-card rounded-md overflow-auto" data-testid="svc-bench-block">
      <div className="px-4 py-3 border-b">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Incheon KIA · Aftersales</div>
        <h3 className="font-heading text-sm font-semibold" data-testid="svc-bench-block-title">{cat} {meta?.label} — per RO comparison <span className="text-muted-foreground font-normal">· {catLabel}</span></h3>
      </div>
      <table className="w-full text-xs">
        <thead className="border-b"><tr>{["Sl", "Dlr code", "Location", "RO", meta?.unit || "Value/RO", "Ranking", "Diff per RO vs group best", "Diff in revenue vs group best"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.code} className="border-b last:border-0 hover:bg-muted/50" data-testid={`svc-bench-row-${r.code}`}>
              <td className="px-3 py-1.5 num text-muted-foreground">{i + 1}</td>
              <td className="px-3 py-1.5 font-semibold">{r.code}</td>
              <td className="px-3 py-1.5">{r.name}</td>
              <td className="px-3 py-1.5 num">{fmtINR(r.ro)}</td>
              <td className="px-3 py-1.5 num font-bold" data-testid={`svc-bench-per-${r.code}`}>{fmtINR(r.per_ro)}</td>
              <td className="px-3 py-1.5"><span className={`inline-flex h-5 min-w-5 px-1 items-center justify-center rounded text-[10px] font-bold ${heat(r.rank, n) || "bg-secondary"}`}>{r.rank}</span></td>
              <td className="px-3 py-1.5 num">{r.diff_per_ro > 0 ? fmtINR(r.diff_per_ro) : <span className="text-emerald-700 font-semibold">best</span>}</td>
              <td className={`px-3 py-1.5 num ${r.diff_rev > 0 ? "text-red-600" : "text-emerald-700"}`}>{fmtINR(r.diff_rev)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t bg-secondary/40 font-semibold">
          <tr data-testid="svc-bench-group">
            <td className="px-3 py-1.5" colSpan={3}>Group</td>
            <td className="px-3 py-1.5 num">{fmtINR(block.group.ro)}</td>
            <td className="px-3 py-1.5 num">{fmtINR(block.group.per_ro)}</td>
            <td />
            <td className="px-3 py-1.5 num">{fmtINR(block.group.diff_per_ro)}</td>
            <td className="px-3 py-1.5 num text-red-600">{fmtINR(block.group.diff_rev)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function Matrix({ cat, blocks, classes, onPick, active, total }) {
  const first = blocks[classes[0].key];
  const rows = [...first.rows].sort((a, b) => a.rank - b.rank);
  const n = total || rows.length;
  const byCode = Object.fromEntries(classes.map((c) => [c.key, Object.fromEntries(blocks[c.key].rows.map((r) => [r.code, r]))]));
  return (
    <div className="border bg-card rounded-md overflow-auto" data-testid="svc-bench-matrix">
      <div className="px-4 py-3 border-b flex items-center gap-3"><h3 className="font-heading text-sm font-semibold">{cat} · per-RO matrix</h3><span className="text-[11px] text-muted-foreground">₹ per RO with rank · click a column to open its block</span></div>
      <table className="w-full text-xs">
        <thead className="border-b">
          <tr>
            <th className={`${TH} sticky left-0 bg-card`}>Workshop</th>
            <th className={`${TH} text-right`}>RO</th>
            {classes.map((c) => <th key={c.key} onClick={() => onPick(c.key)} className={`${TH} text-right cursor-pointer hover:text-foreground ${active === c.key ? "text-foreground underline" : ""}`} data-testid={`svc-matrix-col-${c.key}`}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.code} className="border-b last:border-0 hover:bg-muted/50" data-testid={`svc-matrix-row-${r.code}`}>
              <td className="px-3 py-1 font-medium whitespace-nowrap sticky left-0 bg-card">{r.code} <span className="text-muted-foreground font-normal">{r.name}</span></td>
              <td className="px-3 py-1 num text-right">{r.ro}</td>
              {classes.map((c) => { const x = byCode[c.key][r.code]; return <td key={c.key} className={`px-2 py-1 num text-right ${heat(x.rank, n)}`}><span className="font-semibold">{fmtINR(x.per_ro)}</span> <sup className="text-[9px] opacity-70">{x.rank}</sup></td>; })}
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t bg-secondary/40 font-semibold">
          <tr>
            <td className="px-3 py-1 sticky left-0 bg-secondary/40">Group</td>
            <td className="px-3 py-1 num text-right">{first.group.ro}</td>
            {classes.map((c) => <td key={c.key} className="px-2 py-1 num text-right">{fmtINR(blocks[c.key].group.per_ro)}</td>)}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export default function ServiceBenchmark() {
  const [month, setMonth] = useState(monthOptions()[1]);
  const [ex, setEx] = useExWarranty();
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("GS");
  const [cls, setCls] = useState("revenue");

  const load = useCallback(() => {
    api.get(`/service/benchmark?${qs({ month, ex_warranty: ex })}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e)));
  }, [month, ex]);
  useEffect(load, [load]);
  useEffect(() => { if (data && !data.classes.some((c) => c.key === cls)) setCls("revenue"); }, [data, cls]);

  const catLabel = data?.categories.find((c) => c.key === cat)?.label;
  const block = data?.blocks[cat][cls];
  return (
    <div className="space-y-5" data-testid="service-benchmark">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Benchmark Explorer</h1>
          <p className="text-sm text-muted-foreground">{data ? <>{fmtDay(data.from)} → {fmtDay(data.to)} · 5 RO categories × {data.classes.length} revenue classes · GS {basisText(data.ex_warranty)}</> : "Per-RO comparison across workshops"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <WarrantyToggle ex={ex} onChange={setEx} />
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="svc-bench-month"><SelectValue /></SelectTrigger>
            <SelectContent>{monthOptions().map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/service/export?${qs({ month, ex_warranty: ex })}`} className="inline-flex h-8 items-center gap-1.5 rounded-md bg-foreground text-background px-3 text-xs font-semibold hover:opacity-90 transition-opacity" data-testid="svc-bench-export"><Download size={13} /> Download Excel (gs · bs · summary)</a>
        </div>
      </div>

      {data && block && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center rounded-md border overflow-hidden text-xs" data-testid="svc-bench-cats">
              {data.categories.map((c) => <button key={c.key} onClick={() => setCat(c.key)} className={`px-3 py-1.5 font-semibold transition-colors ${cat === c.key ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`} data-testid={`svc-bench-cat-${c.key}`} title={c.label}>{c.key}</button>)}
            </div>
            <span className="text-xs text-muted-foreground">{catLabel}</span>
            <div className="flex flex-wrap gap-1 ml-auto" data-testid="svc-bench-classes">
              {data.classes.map((c) => <button key={c.key} onClick={() => setCls(c.key)} className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${cls === c.key ? "bg-foreground text-background border-foreground" : "hover:bg-secondary"} ${c.key.startsWith("warranty") ? "border-emerald-300" : ""}`} data-testid={`svc-bench-cls-${c.key}`}>{c.label}</button>)}
            </div>
          </div>
          {data.unclassified_revenue > 0 && <p className="text-[11px] text-amber-800">₹{fmtINR(data.unclassified_revenue)} on unclassified codes is counted as regular labour/parts until classified.</p>}
          <WarrantyStrip w={data.warranty} meta={data.warranty_meta} ex={data.ex_warranty} testId="svc-bench-warranty" />
          <Block cat={cat} cls={cls} block={block} classes={data.classes} catLabel={catLabel} total={data.workshops} />
          <Matrix cat={cat} blocks={data.blocks[cat]} classes={data.classes} onPick={setCls} active={cls} total={data.workshops} />
          <BayPanel bays={data.bays} title="Bay productivity — GS / BP ROs per bay per day" />
        </>
      )}
    </div>
  );
}
